import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  diffSiteAgainstPublic, getPrimaryLinkUrl, getSiteImageUrl, getSiteTitle, getSiteUrl, mergeSite,
  shareActionTitle, shareChangesState, shareResultMessage,
} from '../../common/ItemTools';
import { DELETE_LABEL, UNSHARE_LABEL } from '../../common/ActionLabels';
import {
  bookmarkSite, deleteSite, updateSite, shareSite,
} from '../../redux/features/sites/sitesSlice';
import { bookmarkItem, fetchItemsBySitePreview, shareItem } from '../../redux/features/items/itemsSlice';
import { setSnackbar } from '../../redux/features/snackbarSlice';
import { getNamespacePath } from '../../redux/features/context/contextSlice';
import useConfirm from '../../hooks/useConfirm';
import useGoBack from '../../hooks/useGoBack';
import FitItemGrid from '../FitItemGrid';
import {
  AccentButton, ActionDivider, BackButton, DangerButton, PageBackground, HeroCard, SectionLabel, GroupLabel,
  OutlinedButton, PrimaryButton, TextInput,
} from '../common/hero/styles';
import ViewAllLink from '../common/hero/ViewAllLink';
import ScopeControl from '../common/hero/ScopeControl';
import BookmarkToggle from '../common/hero/BookmarkToggle';
import FavoriteButton from '../common/hero/FavoriteButton';
import ShareButton from '../common/hero/ShareButton';
import HeroVerdictActions from '../common/hero/VerdictActions';
import useVerdict from '../common/hero/useVerdict';
import { factsLine, openUrlInNewTab } from '../common/hero/utils';
import { useFitGrid } from '../common/hero/useFitGrid';
import { useNamespacePreviews } from '../common/hero/useNamespacePreviews';
import HeroIdentityHeader, { HeroAvatar } from '../common/hero/IdentityHeader';
import HeroFacts from '../common/hero/Facts';
import {
  buildDraft, diffDraft, siteInitial,
} from './utils';

export default function SiteHeroPage(props) {
  const {
    site: rawSite, namespace, onRefetch, startInEdit,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { confirm, confirmDialog } = useConfirm();
  const userInfo = useSelector((state) => state.users.userInfo);

  const theme = useTheme();
  const { main: coral } = theme.palette.primary;

  const site = useMemo(() => mergeSite(rawSite) || {}, [rawSite]);
  const shareDiff = useMemo(() => diffSiteAgainstPublic(rawSite), [rawSite]);

  const [editing, setEditing] = useState(Boolean(startInEdit));
  const [draft, setDraft] = useState(() => (startInEdit ? buildDraft(site) : null));
  const [saving, setSaving] = useState(false);

  const isPending = namespace === 'pending';
  const editNamespace = isPending ? 'pending' : 'private';
  const scoped = Boolean(userInfo) && !isPending;

  const { decide, deciding } = useVerdict('site', rawSite.id, getSiteTitle(rawSite));

  // Measure the space under the identity header and fill it with square cards.
  const gridAreaRef = useRef(null);
  const { arrangement, pageSize } = useFitGrid(gridAreaRef, { deps: [editing] });

  const fetchPreview = useCallback((ns, limit) => dispatch(fetchItemsBySitePreview({
    namespace: ns, siteId: rawSite.id, params: { limit },
  })).unwrap(), [dispatch, rawSite.id]);

  const {
    previews, loaded: previewLoaded, activeNamespace, setScope,
    showScope, scopeOptions, recent: preview, refresh: refreshPreviews,
  } = useNamespacePreviews({
    entityId: rawSite.id, namespace, scoped, pageSize, fetchPreview,
  });

  const namespacePath = getNamespacePath(activeNamespace);

  // Edit lifecycle
  const enterEdit = () => {
    setDraft(buildDraft(site));
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };
  const setField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const reportError = (message) => dispatch(setSnackbar({
    snackbarOpen: true, snackbarType: 'error', snackbarMessage: message,
  }));

  const saveEdit = async () => {
    setSaving(true);
    try {
      const patch = diffDraft(site, draft);
      if (Object.keys(patch).length > 0) {
        await dispatch(updateSite({
          namespace: editNamespace, siteId: rawSite.id, patch,
        })).unwrap();
      }
      setEditing(false);
      setDraft(null);
      if (onRefetch) onRefetch();
    } catch (error) {
      reportError('Failed to save the site.');
    } finally {
      setSaving(false);
    }
  };

  // Favorite persists immediately.
  const toggleFavorite = () => {
    dispatch(updateSite({
      namespace: 'private', siteId: rawSite.id, patch: { isFavorite: !site.isFavorite },
    }))
      .unwrap()
      .then(() => { if (onRefetch) onRefetch(); })
      .catch(() => reportError('Failed to favorite the site.'));
  };

  const hasPrivateData = Boolean(rawSite.privateData);
  const hasPublicData = Boolean(rawSite.publicData);
  const share = () => {
    dispatch(shareSite({ siteId: rawSite.id }))
      .unwrap()
      .then((result) => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: shareResultMessage(result),
        }));
        if (onRefetch) onRefetch();
      })
      .catch(() => reportError('Failed to share the site.'));
  };

  const toggleBookmark = async () => {
    if (hasPrivateData) {
      const ok = await confirm({
        title: 'Remove from your library?',
        message: `Your private copy of "${getSiteTitle(rawSite) || 'this site'}" is deleted, along with any changes you made to it. The items from it stay in your library, and the public site stays.`,
        confirmLabel: 'Remove',
        destructive: true,
      });
      if (!ok) return;
      dispatch(deleteSite({ namespace: 'private', siteId: rawSite.id }))
        .unwrap()
        .then(() => {
          refreshPreviews();
          if (onRefetch) onRefetch();
        })
        .catch(() => reportError('Failed to remove the private copy.'));
      return;
    }

    dispatch(bookmarkSite({ siteId: rawSite.id }))
      .unwrap()
      .then(() => onRefetch && onRefetch())
      .catch(() => reportError('Failed to bookmark the site.'));
  };

  const removeSite = async () => {
    const ok = await confirm({
      title: 'Delete site?',
      message: `"${getSiteTitle(rawSite) || 'This site'}" will be permanently removed from your library.`,
      confirmLabel: DELETE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteSite({ namespace: 'private', siteId: rawSite.id }))
      .unwrap()
      .then(goBack)
      .catch(() => reportError('Failed to delete the site.'));
  };

  const unshareSite = async () => {
    const ok = await confirm({
      title: 'Unshare site?',
      message: `"${getSiteTitle(rawSite) || 'This site'}" will be removed from the public record. Its creator keeps their private copy.`,
      confirmLabel: UNSHARE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteSite({ namespace: 'public', siteId: rawSite.id }))
      .unwrap()
      .then(() => {
        if (rawSite.privateData) {
          if (onRefetch) onRefetch();
        } else {
          goBack();
        }
      })
      .catch(() => reportError('Failed to unshare the site.'));
  };

  const shareItemFromCard = (it) => dispatch(shareItem({ itemId: it.id }))
    .unwrap()
    .then((result) => {
      dispatch(setSnackbar({
        snackbarOpen: true, snackbarType: 'success', snackbarMessage: shareResultMessage(result),
      }));
      return refreshPreviews();
    })
    .catch(() => reportError('Failed to share the item.'));

  const bookmarkItemFromCard = (it) => dispatch(bookmarkItem({ itemId: it.id }))
    .unwrap()
    .then(refreshPreviews)
    .catch(() => reportError('Failed to bookmark item.'));

  const itemCardActions = {
    onItemRead: (it) => navigate(isPending ? `/moderate/items/${it.id}` : `/items/${it.id}`),
    onItemOpen: (it) => openUrlInNewTab(getPrimaryLinkUrl(it)),
    onTagOpen: (tagId) => navigate(`/${namespacePath}/items?tag=${tagId}`),
    onAuthorView: (authorId) => navigate(`/authors/${authorId}`),
    onAuthorFilter: (authorId) => navigate(`/${namespacePath}/items?author=${authorId}`),
    onSiteView: (siteId) => navigate(`/sites/${siteId}`),
    onSiteFilter: (siteId) => navigate(`/${namespacePath}/items?site=${siteId}`),
    ...(scoped && {
      onItemShare: shareItemFromCard,
      onItemBookmark: bookmarkItemFromCard,
    }),
  };

  // Derived identity (read mode reads the site; edit mode reflects the draft
  // live so the header updates as the user types).
  const identity = editing && draft ? draft : site;
  const title = identity.title || identity.domain || 'Untitled site';
  const { domain } = identity;
  const hasImage = Boolean(identity.logo || identity.domain);
  const logoUrl = hasImage ? getSiteImageUrl(identity) : null;
  const countsPending = !editing && !previewLoaded;
  // Only the namespace on show is quoted — the scope control beside the line
  // is what switches it.
  const activeData = isPending ? site : rawSite[`${activeNamespace}Data`];
  const facts = factsLine(activeData, previews[activeNamespace]?.itemsCount, activeNamespace);

  const canBookmark = scoped && hasPublicData;
  const canDelete = !isPending && hasPrivateData && !hasPublicData;
  const canUnshare = !isPending && hasPublicData && Boolean(userInfo?.isModerator);
  // A share proposes the private copy - with none there is nothing to offer.
  const canShare = !isPending && hasPrivateData;
  const canDecide = isPending && Boolean(userInfo?.isModerator);

  const readActions = !editing && userInfo ? (
    <>
      {!isPending && (
        <>
          <FavoriteButton
            isFavorite={Boolean(site.isFavorite)}
            onToggle={toggleFavorite}
          />
          {canBookmark && (
            <BookmarkToggle hasPrivateData={hasPrivateData} onToggle={toggleBookmark} />
          )}
          {canShare && (
            <ShareButton
              hasPublicData={hasPublicData}
              changes={shareChangesState(shareDiff)}
              title={shareActionTitle(hasPublicData, shareDiff)}
              onShare={share}
            />
          )}
          <ActionDivider />
        </>
      )}
      {canUnshare && (
        <DangerButton type="button" onClick={unshareSite}>
          <PublicOffIcon sx={{ fontSize: 16 }} />
          {UNSHARE_LABEL}
        </DangerButton>
      )}
      {canDelete && (
        <DangerButton type="button" onClick={removeSite}>
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          {DELETE_LABEL}
        </DangerButton>
      )}
      <AccentButton type="button" onClick={enterEdit}>
        <EditOutlinedIcon sx={{ fontSize: 16 }} />
        Edit
      </AccentButton>
      {canDecide && (
        <>
          <ActionDivider />
          <HeroVerdictActions onDecide={decide} disabled={deciding} />
        </>
      )}
    </>
  ) : null;
  const headerActions = editing ? (
    <>
      <OutlinedButton type="button" onClick={cancelEdit} disabled={saving}>Cancel</OutlinedButton>
      <PrimaryButton type="button" onClick={saveEdit} disabled={saving}>
        <CheckIcon sx={{ fontSize: 16 }} />
        Save
      </PrimaryButton>
    </>
  ) : readActions;

  // Never render more than the current page holds, so a shrink-resize can't
  // briefly overflow the grid with stale items before the refetch lands.
  const displayItems = pageSize ? preview.items.slice(0, pageSize) : preview.items;
  const itemCount = displayItems.length;
  const displayArrangement = arrangement && itemCount > 0 && itemCount <= arrangement.cols
    ? { cols: Math.max(2, itemCount), rows: 1 }
    : arrangement;

  return (
    <PageBackground sx={{ padding: 0 }}>
      <HeroCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Action bar — wider than the content column, so Back and the page's
            actions sit outside the block they act on rather than on its edges */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 28px 0',
          width: '100%',
          maxWidth: 1400,
          mx: 'auto',
          flexShrink: 0,
        }}>
          <BackButton type="button" onClick={goBack}>
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
            <span>Back</span>
          </BackButton>
          <Box sx={{ flex: 1 }} />
          {headerActions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {headerActions}
            </Box>
          )}
        </Box>

        {/* Content column — scrolls internally when the content doesn't fit,
            keeping the action bar pinned */}
        <Box sx={{
          width: '100%',
          maxWidth: 1232,
          mx: 'auto',
          padding: '24px 28px 24px',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}>
          {/* Identity header (identical in both modes) */}
          <HeroIdentityHeader
            avatar={(
              <HeroAvatar imageUrl={logoUrl} fit="contain" background={coral}>
                {siteInitial(identity)}
              </HeroAvatar>
            )}
            title={title}
            subtitle={domain ? (
              <Box
                component="span"
                role="button"
                onClick={() => openUrlInNewTab(getSiteUrl(identity))}
                sx={{ cursor: 'pointer', '&:hover': { color: coral } }}
              >
                {domain}
              </Box>
            ) : null}
          />

          {/* Edit form (edit mode) */}
          {editing && draft && (
            <Box sx={{ mt: '24px', flexShrink: 0 }}>
              <SectionLabel>Site details</SectionLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Box>
                  <GroupLabel>Title</GroupLabel>
                  <TextInput
                    style={{ width: '100%' }}
                    value={draft.title}
                    onChange={(e) => setField('title', e.target.value)}
                  />
                </Box>
                <Box>
                  <GroupLabel>Domain</GroupLabel>
                  <TextInput
                    style={{ width: '100%' }}
                    value={draft.domain}
                    onChange={(e) => setField('domain', e.target.value)}
                  />
                </Box>
                <Box>
                  <GroupLabel>Logo URL</GroupLabel>
                  <TextInput
                    style={{ width: '100%' }}
                    value={draft.logo}
                    onChange={(e) => setField('logo', e.target.value)}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Site items (read mode): fills the rest of the page height, cards
              fit the space like the item views. */}
          {!editing && (
            <Box
              ref={gridAreaRef}
              sx={{
                flex: 1,
                minHeight: 240,
                display: 'flex',
                flexDirection: 'column',
                mt: '18px',
              }}
            >
              {displayItems.length > 0 && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 30,
                  gap: '14px',
                  flexShrink: 0,
                }}>
                  <SectionLabel sx={{ mb: 0 }}>Site items</SectionLabel>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0,
                  }}>
                    <HeroFacts facts={facts} loading={countsPending} />
                    {showScope && (
                      <ScopeControl
                        options={scopeOptions}
                        value={activeNamespace}
                        onChange={setScope}
                      />
                    )}
                    <ViewAllLink
                      title={`View all ${preview.itemsCount} items from ${title}`}
                      onClick={() => navigate(`/${namespacePath}/items?site=${rawSite.id}`)}
                    />
                  </Box>
                </Box>
              )}
              {displayItems.length > 0 && (
                <FitItemGrid
                  items={displayItems}
                  namespace={activeNamespace}
                  itemActions={itemCardActions}
                  arrangement={displayArrangement}
                  isPreview
                />
              )}
            </Box>
          )}
        </Box>
      </HeroCard>

      {confirmDialog}
    </PageBackground>
  );
}

SiteHeroPage.propTypes = {
  site: PropTypes.object.isRequired,
  namespace: PropTypes.string,
  onRefetch: PropTypes.func,
  startInEdit: PropTypes.bool,
};

SiteHeroPage.defaultProps = {
  namespace: 'public',
  onRefetch: null,
  startInEdit: false,
};
